import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, X } from "lucide-react";
import { useAuth } from "../scripts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getNotificationBody,
  getNotificationPath,
  getNotificationTitle,
  shouldOpenDmDrawer,
} from "@/lib/notificationRoutes";
import { useDmDrawer } from "../scripts/DmDrawerContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import { useOptionalNotificationsSocket } from "../scripts/NotificationsSocketContext";
import { listDmConversations } from "@/lib/dmApi";
import { toastError } from "@/lib/toast";

import { API_BASE_URL } from "@/lib/apiBase";
const API = `${API_BASE_URL}`;
const isDev = process.env.NODE_ENV === "development";

const NotificationBell = () => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const { openConversation } = useDmDrawer();
  const { setSelectedOrgId, selectedOrgId, workspaceSnapshot, workspaceRefreshKey } =
    useOrgFilter();
  const notificationsSocket = useOptionalNotificationsSocket();
  const notificationsSubscribe = notificationsSocket?.subscribe;
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const keepOpenOnDismissRef = useRef(false);

  const workOrgId =
    selectedOrgId && selectedOrgId !== "social" ? selectedOrgId : null;

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const fetchUnreadCount = useCallback(async () => {
    if (!accessToken) return;
    if (!workOrgId) {
      setUnreadCount(0);
      return;
    }
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, {
        headers: authHeaders(),
        params: { org_id: workOrgId },
      });
      setUnreadCount(Number(res.data?.count) || 0);
    } catch (err) {
      console.error("Failed to fetch unread notification count:", err);
    }
  }, [accessToken, authHeaders, workOrgId]);

  // Seed from workspace enter snapshot (avoid an extra GET on join)
  useEffect(() => {
    if (!workOrgId) {
      setUnreadCount(0);
      return;
    }
    if (
      workspaceSnapshot?.orgId === workOrgId &&
      workspaceSnapshot.unread &&
      typeof workspaceSnapshot.unread.notifications === "number"
    ) {
      setUnreadCount(Number(workspaceSnapshot.unread.notifications) || 0);
    }
  }, [workOrgId, workspaceSnapshot, workspaceRefreshKey]);

  const fetchList = useCallback(async () => {
    if (!accessToken) return;
    if (!workOrgId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: authHeaders(),
        params: { limit: 30, offset: 0, org_id: workOrgId },
      });
      setItems(
        (Array.isArray(res.data?.items) ? res.data.items : []).filter(
          (n) => n.type !== "dm.message" && n.type !== "dm.reaction"
        )
      );
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, authHeaders, workOrgId]);

  useEffect(() => {
    if (!accessToken) {
      setUnreadCount(0);
      setItems([]);
      return;
    }
    fetchUnreadCount();
  }, [accessToken, fetchUnreadCount]);

  useEffect(() => {
    if (open && accessToken) {
      fetchList();
      fetchUnreadCount();
    }
  }, [open, accessToken, fetchList, fetchUnreadCount]);

  useEffect(() => {
    if (!notificationsSubscribe) return undefined;
    return notificationsSubscribe((event, payload) => {
      if (event === "notification.unread_count") {
        if (
          workOrgId &&
          payload?.org_id &&
          String(payload.org_id) !== String(workOrgId)
        ) {
          return;
        }
        if (payload?.count != null) {
          setUnreadCount(Number(payload.count) || 0);
        } else {
          fetchUnreadCount();
        }
        return;
      }
      if (event === "notification.created") {
        const n = payload?.notification;
        if (!n) return;
        if (n.type === "dm.message" || n.type === "dm.reaction") return;
        if (
          workOrgId &&
          n.org_id &&
          String(n.org_id) !== String(workOrgId)
        ) {
          return;
        }
        if (payload?.unread_count != null) {
          setUnreadCount(Number(payload.unread_count) || 0);
        } else {
          fetchUnreadCount();
        }
        if (open) {
          setItems((prev) => {
            const id = n.notification_id ?? n.notificationId;
            if (
              prev.some(
                (x) => (x.notification_id ?? x.notificationId) === id
              )
            ) {
              return prev.map((x) =>
                (x.notification_id ?? x.notificationId) === id ? n : x
              );
            }
            return [n, ...prev].slice(0, 30);
          });
        }
      }
    });
  }, [
    notificationsSubscribe,
    workOrgId,
    open,
    fetchUnreadCount,
  ]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, null, {
        headers: authHeaders(),
      });
      setItems((prev) =>
        prev.map((n) =>
          (n.notification_id ?? n.notificationId) === id
            ? { ...n, is_read: true, isRead: true }
            : n
        )
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error("Failed to mark notification read:", err);
    }
  };

  const markAllRead = async () => {
    if (!workOrgId) return;
    try {
      await axios.post(`${API}/notifications/read-all`, null, {
        headers: authHeaders(),
        params: { org_id: workOrgId },
      });
      setItems((prev) =>
        prev.map((n) => ({ ...n, is_read: true, isRead: true }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all notifications read:", err);
    }
  };

  const deleteNotification = async (id, wasUnread) => {
    if (!id || deletingId === id) return;
    const keepOpen = items.length > 1;
    if (keepOpen) {
      keepOpenOnDismissRef.current = true;
      setOpen(true);
    }
    setDeletingId(id);
    try {
      await axios.delete(`${API}/notifications/${id}`, {
        headers: authHeaders(),
      });
      setItems((prev) =>
        prev.filter((n) => (n.notification_id ?? n.notificationId) !== id)
      );
      if (wasUnread) {
        setUnreadCount((c) => Math.max(0, c - 1));
      }
      if (keepOpen) {
        setOpen(true);
      }
    } catch (err) {
      console.error("Failed to delete notification:", err);
      toastError(err, "Failed to clear notification");
      if (keepOpen) {
        setOpen(true);
      }
    } finally {
      setDeletingId(null);
      if (keepOpen) {
        requestAnimationFrame(() => {
          keepOpenOnDismissRef.current = false;
          setOpen(true);
        });
      }
    }
  };

  const handleItemClick = async (n) => {
    const id = n.notification_id ?? n.notificationId;
    const unread = !(n.is_read ?? n.isRead);
    if (unread && id) {
      await markRead(id);
    }
    const path = getNotificationPath(n);
    setOpen(false);

    if (shouldOpenDmDrawer(n) && path) {
      const payload = n.payload || {};
      const orgId = n.org_id ?? n.orgId ?? payload.org_id;
      const conversationId =
        payload.conversation_id ?? n.entity_id ?? n.entityId;
      if (orgId) {
        setSelectedOrgId(String(orgId));
      }
      const isDesktop =
        typeof window !== "undefined" &&
        window.matchMedia("(min-width: 768px)").matches;
      if (isDesktop && orgId && conversationId && accessToken) {
        try {
          const list = await listDmConversations(accessToken, orgId);
          const conv = list.find(
            (c) => Number(c.conversation_id) === Number(conversationId)
          );
          openConversation(conversationId, { peer: conv?.peer || null });
          return;
        } catch (err) {
          console.error("Failed to open DM drawer from notification:", err);
        }
      }
    }

    if (path) {
      navigate(path);
    }
  };

  const handleSeed = async () => {
    if (!isDev || seeding) return;
    setSeeding(true);
    try {
      await axios.post(
        `${API}/notifications/dev/seed`,
        {},
        { headers: authHeaders() }
      );
      await fetchList();
      await fetchUnreadCount();
    } catch (err) {
      console.error("Failed to seed notification:", err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to seed notification";
      toastError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSeeding(false);
    }
  };

  if (!accessToken) return null;

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        if (!next && keepOpenOnDismissRef.current) {
          setOpen(true);
          return;
        }
        setOpen(next);
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 flex items-center justify-center p-0 text-[10px]"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[min(20rem,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel className="flex items-center justify-between gap-2">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <button
              type="button"
              className="text-xs font-normal text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                markAllRead();
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              Mark all read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {loading && items.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            No notifications yet.
          </div>
        ) : (
          <div className="flex flex-col gap-0.5 py-1">
            {items.map((n) => {
              const id = n.notification_id ?? n.notificationId;
              const unread = !(n.is_read ?? n.isRead);
              return (
                <div
                  key={id}
                  className="flex items-start gap-2 rounded-sm px-2 py-2 hover:bg-accent"
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    onClick={() => handleItemClick(n)}
                  >
                    {unread ? (
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    ) : (
                      <span className="mt-1.5 h-2 w-2 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug">
                        {getNotificationTitle(n)}
                      </p>
                      {getNotificationBody(n) && (
                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                          {getNotificationBody(n)}
                        </p>
                      )}
                      {(n.created_at || n.createdAt) && (
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          {new Date(
                            n.created_at || n.createdAt
                          ).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </button>
                  <button
                    type="button"
                    aria-label="Clear notification"
                    title="Clear"
                    disabled={deletingId === id}
                    className="shrink-0 rounded-sm p-1 text-muted-foreground hover:bg-background hover:text-foreground"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteNotification(id, unread);
                    }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {isDev && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs"
                disabled={seeding}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSeed();
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {seeding ? "Adding…" : "Add sample (dev)"}
              </Button>
            </div>
          </>
        )}
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5">
          <button
            type="button"
            className="w-full text-left text-xs text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
              navigate("/profile?tab=settings");
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            Notification settings
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
